/* ==========================================================================
   LINDIE Fiction Rows — horizontal scrolling

   Adapted from the pattern in section-lindie-category-grid.js; that file is
   NOT modified and is not a dependency. This script only ever touches
   elements inside [data-lfr-section].

   Behaviour:
     - a back and a forward arrow per row, each with its own edge fade
     - a click advances exactly one card; scroll-snap settles the rest on the
       nearest card edge, so a card is never left half-cut
     - each arrow is hidden at the end it points toward, and both are hidden
       when the content already fits and there is nothing to scroll
     - re-binds on shopify:section:load so it works in the theme editor
   ========================================================================== */
(function () {
  'use strict';

  var EDGE = 2; // px tolerance for "is at the end"

  function trackGap(track) {
    var gap = parseFloat(window.getComputedStyle(track).columnGap);
    if (isNaN(gap)) gap = parseFloat(window.getComputedStyle(track).gap);
    return isNaN(gap) ? 0 : gap;
  }

  function cardStep(track) {
    var item = track.querySelector('.lfr-row__item');
    if (!item) return 0;
    return item.getBoundingClientRect().width + trackGap(track);
  }

  /* One card per click. The track also carries scroll-snap, which settles the
     rest on the nearest card edge. */
  function scrollStep(track) {
    return cardStep(track) || track.clientWidth;
  }

  function maxScroll(track) {
    return Math.max(track.scrollWidth - track.clientWidth, 0);
  }

  function update(row) {
    var track = row.querySelector('[data-lfr-track]');
    var next = row.querySelector('[data-lfr-next]');
    var prev = row.querySelector('[data-lfr-prev]');
    if (!track || !next) return;

    var max = maxScroll(track);
    var scrollable = max > EDGE;
    var atEnd = track.scrollLeft >= max - EDGE;
    var atStart = track.scrollLeft <= EDGE;

    /* Hidden when there is nothing more to scroll — including the case where
       all cards already fit, so no arrow ever appears on a short row. */
    next.hidden = !scrollable || atEnd;

    /* The back arrow appears only once the row has actually been scrolled, so
       a row at rest looks exactly as it did before. */
    if (prev) prev.hidden = !scrollable || atStart;

    /* Each edge fade follows exactly the same state as the arrow on that side. */
    row.classList.toggle('lfr-row--has-more', !next.hidden);
    row.classList.toggle('lfr-row--has-prev', !!prev && !prev.hidden);
  }

  function bind(row) {
    if (!row || row.dataset.lfrBound === 'true') return;

    var track = row.querySelector('[data-lfr-track]');
    var next = row.querySelector('[data-lfr-next]');
    var prev = row.querySelector('[data-lfr-prev]');
    if (!track || !next) return;

    row.dataset.lfrBound = 'true';

    var refresh = function () { update(row); };

    next.addEventListener('click', function () {
      track.scrollBy({ left: scrollStep(track), behavior: 'smooth' });
    });

    if (prev) {
      prev.addEventListener('click', function () {
        track.scrollBy({ left: -scrollStep(track), behavior: 'smooth' });
      });
    }

    track.addEventListener('scroll', refresh, { passive: true });

    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(function () {
        window.requestAnimationFrame(refresh);
      });
      ro.observe(track);
      track.querySelectorAll('img').forEach(function (img) { ro.observe(img); });
    } else {
      window.addEventListener('resize', refresh, { passive: true });
    }

    /* Cover art changes the track width as it loads. */
    track.querySelectorAll('img').forEach(function (img) {
      if (!img.complete) img.addEventListener('load', refresh, { once: true });
    });

    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(refresh);
    });

    window.addEventListener('load', refresh, { once: true });
    window.addEventListener('resize', refresh, { passive: true });
  }

  /* ── Batched row loading ─────────────────────────────────────────────────
     The server renders the first N rows of each tab in full. Every row after
     that ships as chrome only — heading, See All, empty track — hidden, and
     carrying the endpoint its cards come from. As the reader nears the end of
     what is loaded, the next N are fetched and revealed.

     Each panel is queried separately, so Fiction and Non-Fiction keep entirely
     independent queues and progress. Menu order is fixed server-side: batches
     only fill rows that are already in place, so nothing can reorder.

     Cards come from templates/collection.lfr-cards.liquid, which renders the
     same card snippet as the section. The cart and preview handlers are
     delegated at document (lindie-cart.js / lindie-audio-preview.js), so
     injected cards need no re-initialisation — only the carousel does.
     ───────────────────────────────────────────────────────────────────────── */

  function batchSize(el) {
    var section = el.closest ? el.closest('[data-lfr-section]') : null;
    var n = section ? parseInt(section.getAttribute('data-lfr-batch-size'), 10) : NaN;
    return isNaN(n) || n < 1 ? 6 : n;
  }

  /* Rows still waiting for their cards, in document (menu) order. */
  function pendingRows(panel) {
    return [].slice.call(panel.querySelectorAll('[data-lfr-row][data-lfr-row-src]'));
  }

  function loadRow(row) {
    var src = row.getAttribute('data-lfr-row-src');
    /* dataset state is the duplicate guard: a row is only ever fetched once. */
    if (!src || row.dataset.lfrRowState) return Promise.resolve();
    row.dataset.lfrRowState = 'loading';

    return fetch(src, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var track = row.querySelector('[data-lfr-track]');
        if (!track) throw new Error('no track');

        var holder = document.createElement('ul');
        holder.innerHTML = html;

        var items = holder.querySelectorAll('.lfr-row__item');
        if (!items.length) throw new Error('no items returned');

        /* The endpoint serves a fixed upper bound; keep what this row asked for. */
        var limit = parseInt(row.getAttribute('data-lfr-row-count'), 10);
        if (isNaN(limit) || limit < 1) limit = items.length;

        var frag = document.createDocumentFragment();
        for (var i = 0; i < items.length && i < limit; i++) frag.appendChild(items[i]);
        track.appendChild(frag);

        row.removeAttribute('data-lfr-row-src');
        row.dataset.lfrRowState = 'loaded';
        row.hidden = false;

        /* Only the carousel needs wiring; bind() is idempotent. */
        bind(row);
        update(row);

        /* Cover art changes the track width as it decodes. */
        track.querySelectorAll('img').forEach(function (img) {
          if (!img.complete) img.addEventListener('load', function () { update(row); }, { once: true });
        });
      })
      .catch(function () {
        row.dataset.lfrRowState = 'failed';
        row.removeAttribute('data-lfr-row-src');
        /* A row that never received cards must not leave a stray heading. */
        if (!row.querySelector('.lac-card')) row.remove();
      });
  }

  function loadNextBatch(panel) {
    if (panel.dataset.lfrBatchBusy === 'true') return Promise.resolve();

    var queue = pendingRows(panel).slice(0, batchSize(panel));
    if (!queue.length) return Promise.resolve();

    panel.dataset.lfrBatchBusy = 'true';

    return Promise.all(queue.map(loadRow)).then(function () {
      panel.dataset.lfrBatchBusy = '';
      /* Re-arm against the new last row; watchPanel stops once none are left. */
      watchPanel(panel);
    });
  }

  var batchObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          batchObserver.unobserve(entry.target);
          var panel = entry.target.closest('[data-lfr-panel]');
          if (panel) loadNextBatch(panel);
        });
      }, { rootMargin: '600px 0px' })
    : null;

  function watchPanel(panel) {
    /* A hidden panel has no box and can never intersect; it is re-armed when
       its tab is opened. */
    if (!panel || panel.hidden) return;

    /* End of list: nothing left to fetch, so nothing is observed and the
       loader goes quiet for this tab. */
    if (!pendingRows(panel).length) return;

    var loaded = panel.querySelectorAll('[data-lfr-row]:not([hidden])');
    var anchor = loaded[loaded.length - 1];

    if (!anchor) { loadNextBatch(panel); return; }
    if (batchObserver) batchObserver.observe(anchor);
    else loadNextBatch(panel);
  }

  function watchPanels(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-lfr-panel]').forEach(watchPanel);
  }

  /* ── Fiction / Non-Fiction tabs ──────────────────────────────────────────
     Purely a show/hide of panels that are already rendered: the rows come
     from the navigation menu behind each tab, and switching fetches nothing.
     Everything is scoped to one [data-lfr-section].
     ───────────────────────────────────────────────────────────────────── */

  function activateTab(section, type, moveFocus) {
    var tabs = section.querySelectorAll('[data-lfr-tab]');
    var panels = section.querySelectorAll('[data-lfr-panel]');

    tabs.forEach(function (tab) {
      var on = tab.getAttribute('data-lfr-tab') === type;
      tab.classList.toggle('lfr-tabs__tab--active', on);
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      /* Roving tabindex: only the selected tab is in the tab order. */
      tab.tabIndex = on ? 0 : -1;
      if (on && moveFocus) tab.focus();
    });

    panels.forEach(function (panel) {
      /* [hidden] removes the inactive panel from the accessibility tree as
         well as from view. */
      panel.hidden = panel.getAttribute('data-lfr-panel') !== type;
    });

    /* The revealed panel could not intersect while hidden, so start (or
       resume) its batch loading now. */
    watchPanels(section);

    /* Rows in a previously hidden panel had no layout, so their arrow state
       was computed against a zero-width track. Recalculate now they are on
       screen. */
    section.querySelectorAll('[data-lfr-panel]:not([hidden]) [data-lfr-row]').forEach(function (row) {
      bind(row);
      update(row);
    });
  }

  function bindTabs(section) {
    if (!section || section.dataset.lfrTabsBound === 'true') return;

    var tabs = [].slice.call(section.querySelectorAll('[data-lfr-tab]'));
    var panels = section.querySelectorAll('[data-lfr-panel]');
    if (!tabs.length || !panels.length) return;

    section.dataset.lfrTabsBound = 'true';

    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        activateTab(section, tab.getAttribute('data-lfr-tab'), false);
      });

      /* WAI-ARIA tabs keyboard pattern. */
      tab.addEventListener('keydown', function (event) {
        var i = tabs.indexOf(tab);
        var next = -1;

        if (event.key === 'ArrowRight') next = (i + 1) % tabs.length;
        else if (event.key === 'ArrowLeft') next = (i - 1 + tabs.length) % tabs.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = tabs.length - 1;

        if (next < 0) return;
        event.preventDefault();
        activateTab(section, tabs[next].getAttribute('data-lfr-tab'), true);
      });
    });
  }

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;

    scope.querySelectorAll('[data-lfr-section]').forEach(bindTabs);

    scope.querySelectorAll('[data-lfr-row]').forEach(function (row) {
      bind(row);
      update(row);
    });

    watchPanels(scope);

    /* Arriving from a "See All" page's tab link: open the tab that was clicked
       rather than the server-rendered default. The choice is handed over in
       sessionStorage because that link's URL carries no hash and is identical
       for both tabs. A #fiction / #non-fiction hash still works for any older
       link, but the handover wins when both are present — it is the more
       deliberate signal. */
    if (!openFromHandoff(scope)) openFromHash(scope);
  }

  var HANDOFF_KEY = 'lindie_open_tab';

  function openFromHandoff(root) {
    var wanted = null;

    try {
      wanted = window.sessionStorage.getItem(HANDOFF_KEY);
      /* read once: a later plain visit must get the default tab */
      if (wanted) window.sessionStorage.removeItem(HANDOFF_KEY);
    } catch (e) {
      return false; /* storage blocked — fall through to the default tab */
    }

    if (wanted !== 'fiction' && wanted !== 'non-fiction') return false;

    var scope = root && root.querySelectorAll ? root : document;
    var opened = false;

    scope.querySelectorAll('[data-lfr-section]').forEach(function (section) {
      if (section.querySelector('[data-lfr-panel="' + wanted + '"]')) {
        activateTab(section, wanted, false);
        opened = true;
      }
    });

    return opened;
  }

  function openFromHash(root) {
    var scope = root && root.querySelectorAll ? root : document;

    var wanted = (window.location.hash || '').replace('#', '');
    if (wanted !== 'fiction' && wanted !== 'non-fiction') return;

    scope.querySelectorAll('[data-lfr-section]').forEach(function (section) {
      if (section.querySelector('[data-lfr-panel="' + wanted + '"]')) {
        activateTab(section, wanted, false);
      }
    });
  }

  /* A hash-only change does not reload the page, so back/forward between
     #fiction and #non-fiction has to be picked up separately. */
  window.addEventListener('hashchange', function () { openFromHash(document); });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); }, { once: true });
  } else {
    init(document);
  }

  /* Theme editor: sections are re-rendered, so re-bind the new DOM. */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target || document);
  });
})();
